/**
 * Shared shapes for the batched write path (POST /commands on the app-server).
 *
 * One op is exactly what namedCommand takes, so an accumulated list can be
 * flushed as a batch or replayed one at a time without reshaping anything.
 */
export interface NamedCommandOp {
  name: string;
  params: Record<string, unknown>;
}

/**
 * Per-op outcome, in request order. `kind` mirrors the sync layer's own
 * ApplyResult: "ack" means the row landed, "reject" means it did not.
 *
 * Branch on the FIELDS, never on a status code or on `reason`'s text — the
 * single-row route spends 409, 500 and 503 on different rejects and that
 * enumeration has already been overtaken several times.
 */
export interface NamedCommandResult {
  name: string;
  rowId: string;
  kind: "ack" | "reject";
  serverSeq?: number;
  reason?: string;
  retryable?: boolean;
  terminal?: boolean;
  /** Kernel holds the row but the local projection refused it: not retryable. */
  serverFault?: boolean;
}

/**
 * A reject that means "the server already holds an equal-or-newer row", i.e.
 * the write was a no-op and nothing is wrong. Everything else is a real fault.
 *
 * This is the batch twin of isBenignLwwReject, which reads a 409 body. Here the
 * reason arrives already classified, so no status is involved.
 */
export function isBenignBatchReject(r: NamedCommandResult): boolean {
  return r.kind === "reject" && r.terminal === true && r.reason === "lww";
}
